import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, take } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { PreferenciaNotificacaoModel } from 'src/app/core/models/preferencia-notificacao.model';
import { TipoNotificacaoEnum } from 'src/app/core/enums/tipo-notificacao.enum';
import {
  ORDEM_CATEGORIAS_PREFERENCIA_NOTIFICACAO,
  PREFERENCIA_NOTIFICACAO_CATALOG,
} from 'src/app/core/constants/preferencia-notificacao-catalog';
import { AuthService } from 'src/app/shared/services/auth.service';
import { AppLocalStorageService } from 'src/app/shared/services/app-local-storage.service';
import { PerfilService } from 'src/app/modules/perfil/perfil.service';
import { PopupService } from 'src/app/shared/services/popup.service';

const STORAGE_KEY_PREFIX = 'sgr_pref_notific';

import { SgrResponse } from 'src/app/core/models/response.model';
import { Usuario } from 'src/app/core/models/usuario.model';

@Injectable({
  providedIn: 'root',
})
export class PreferenciaNotificacaoService {
  private preferenciasAtuais: PreferenciaNotificacaoModel[] = [];
  private preferenciasSubject: BehaviorSubject<PreferenciaNotificacaoModel[]>;

  constructor(
    private authService: AuthService,
    private localStorageService: AppLocalStorageService,
    private perfilService: PerfilService,
    private popupService: PopupService
  ) {
    this.preferenciasAtuais = this.mesclarCatalogoComArmazenamento();
    this.preferenciasSubject = new BehaviorSubject<PreferenciaNotificacaoModel[]>([...this.preferenciasAtuais]);
  }

  /**
   * Recarrega preferências: busca no servidor (fonte de verdade) e replica no localStorage,
   * depois aplica o catálogo. Necessário após login, pois a tela de login limpa o localStorage.
   */
  refresh(): void {
    const usuario = this.usuarioLogadoAtual();
    if (!usuario?.CPF) {
      this.recarregarPreferenciasInterno();
      return;
    }

    this.perfilService
      .buscarPreferencias({ spinnerOff: true })
      .pipe(
        take(1),
        catchError(() => of(null))
      )
      .subscribe((resp: SgrResponse) => {
        if (resp && resp['error'] === false && resp['data']?.['preferenciasNotificacao'] !== undefined) {
          const merged = this.mesclarRespostaServidorComCatalogo(resp['data']['preferenciasNotificacao']);
          this.localStorageService.setItem(this.storageKey(usuario.CPF), JSON.stringify(merged));
        }
        this.recarregarPreferenciasInterno();
      });
  }

  /** Converte o mapa do backend (ou objeto vazio) no mapa completo por tipo, alinhado ao catálogo. */
  private mesclarRespostaServidorComCatalogo(servidor: unknown): Record<string, boolean> {
    const map: Record<string, boolean> =
      servidor && typeof servidor === 'object' && !Array.isArray(servidor)
        ? (servidor as Record<string, boolean>)
        : {};

    const definicoes = [...PREFERENCIA_NOTIFICACAO_CATALOG].sort((a, b) => a.ordem - b.ordem);
    const out: Record<string, boolean> = {};
    for (const def of definicoes) {
      const chave = def.tipo as string;
      if (def.obrigatorio) {
        out[chave] = true;
        continue;
      }
      if (Object.prototype.hasOwnProperty.call(map, chave)) {
        out[chave] = !!map[chave];
      } else {
        out[chave] = def.defaultHabilitado ?? true;
      }
    }
    return out;
  }

  getPreferencias(): Observable<PreferenciaNotificacaoModel[]> {
    return this.preferenciasSubject.asObservable();
  }

  /**
   * Indica se o tipo está habilitado para o usuário atual (default: true se não houver registro).
   * Use antes de exibir ou tratar notificações no cliente.
   */
  estaTipoHabilitado(tipo: TipoNotificacaoEnum): boolean {
    const pref = this.preferenciasAtuais.find(p => p.tipo === tipo);
    if (!pref) {
      return true;
    }
    return pref.habilitado;
  }

  atualizarPreferencia(tipo: TipoNotificacaoEnum, habilitado: boolean): void {
    const idx = this.preferenciasAtuais.findIndex(p => p.tipo === tipo);
    if (idx === -1 || this.preferenciasAtuais[idx].obrigatorio) {
      return;
    }

    const habilitadoAnterior = this.preferenciasAtuais[idx].habilitado;
    if (habilitadoAnterior === habilitado) {
      return;
    }

    this.preferenciasAtuais[idx] = {
      ...this.preferenciasAtuais[idx],
      habilitado,
    };
    this.persistirEEmitir();

    this.sincronizarComServidorOpcional(() => {
      this.preferenciasAtuais[idx] = {
        ...this.preferenciasAtuais[idx],
        habilitado: habilitadoAnterior,
      };
      this.persistirEEmitir();
      this.popupService.showError('Não foi possível salvar suas preferências de notificação. Tente novamente.');
    });
  }

  private storageKey(cpf: string): string {
    return `${STORAGE_KEY_PREFIX}_${cpf}`;
  }

  private recarregarPreferenciasInterno(): void {
    this.preferenciasAtuais = this.mesclarCatalogoComArmazenamento();
    this.preferenciasSubject.next([...this.preferenciasAtuais]);
  }

  private mesclarCatalogoComArmazenamento(): PreferenciaNotificacaoModel[] {
    const usuario = this.usuarioLogadoAtual();
    let salvo: Record<string, boolean> = {};
    if (usuario?.CPF) {
      const raw = this.localStorageService.getItem(this.storageKey(usuario.CPF));
      if (raw) {
        try {
          salvo = JSON.parse(raw) as Record<string, boolean>;
        } catch {
          salvo = {};
        }
      }
    }

    const definicoes = [...PREFERENCIA_NOTIFICACAO_CATALOG].sort((a, b) => a.ordem - b.ordem);
    return definicoes.map(def => {
      const chave = def.tipo as string;
      const habilitadoPadrao = def.defaultHabilitado ?? true;
      const habilitado = def.obrigatorio
        ? true
        : salvo[chave] !== undefined
          ? salvo[chave]
          : habilitadoPadrao;
      return {
        tipo: def.tipo,
        titulo: def.titulo,
        descricao: def.descricao,
        categoria: def.categoria,
        habilitado,
        obrigatorio: def.obrigatorio,
      };
    });
  }

  private persistirEEmitir(): void {
    const usuario = this.usuarioLogadoAtual();
    if (usuario?.CPF) {
      const map: Record<string, boolean> = {};
      this.preferenciasAtuais.forEach(p => {
        map[p.tipo as string] = p.habilitado;
      });
      this.localStorageService.setItem(this.storageKey(usuario.CPF), JSON.stringify(map));
    }
    this.preferenciasSubject.next([...this.preferenciasAtuais]);
  }

  private usuarioLogadoAtual(): Usuario | null {
    return this.authService.isTokenValid() ? this.authService.usuarioLogado() : null;
  }

  /** Envia payload ao backend e permite reverter o estado local em caso de falha. */
  private sincronizarComServidorOpcional(onError: () => void): void {
    const map: Record<string, boolean> = {};
    this.preferenciasAtuais.forEach(p => {
      map[p.tipo as string] = p.habilitado;
    });
    const body = JSON.stringify({ preferenciasNotificacao: map });

    this.perfilService
      .salvarPreferencias({ body, spinnerOff: true })
      .pipe(
        take(1),
        catchError(() => {
          onError();
          return of(null);
        })
      )
      .subscribe((response: SgrResponse) => {
        if (!response || response.error) {
          onError();
        }
      });
  }
}

/** Agrupa itens por categoria na ordem definida no catálogo. */
export function agruparPreferenciasNotificacao(
  preferencias: PreferenciaNotificacaoModel[]
): { categoria: string; itens: PreferenciaNotificacaoModel[] }[] {
  const porCategoria = new Map<string, PreferenciaNotificacaoModel[]>();
  for (const p of preferencias) {
    const cat = p.categoria || 'Outros';
    if (!porCategoria.has(cat)) {
      porCategoria.set(cat, []);
    }
    porCategoria.get(cat)!.push(p);
  }
  return ORDEM_CATEGORIAS_PREFERENCIA_NOTIFICACAO.filter(c => porCategoria.has(c)).map(c => ({
    categoria: c,
    itens: porCategoria.get(c)!,
  }));
}
